import {
  can,
  type Contact,
  ContactSchema,
  type FormDetail,
  PAYMENT_LABELS,
  PAYMENT_TAGS,
  type PaymentTag,
} from '@atlas-control/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Lock, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuditTimeline } from '@/components/audit-timeline';
import { Pagination } from '@/components/pagination';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { PageHeader } from '@/components/page-header';
import { ReviewCard } from '@/components/review-card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formQuery, historyQuery, useFormMutations } from '@/features/forms/api';
import { PaymentBadge, ReviewBadge } from '@/features/forms/badges';
import { IntakeEditor } from '@/features/forms/intake-editor';
import { IntakeView } from '@/features/forms/intake-view';
import { StartRunButton } from '@/features/runs/start-run-button';
import { useMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/format';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/forms/$formId')({
  beforeLoad: ({ context }) => guard(context.me, 'forms.read'),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(formQuery(params.formId)),
  component: FormPage,
});

function FormPage() {
  const { formId } = Route.useParams();
  const { data: form } = useSuspenseQuery(formQuery(formId));

  return (
    <>
      <PageHeader
        title={form.company}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {form.source === 'manual'
              ? `Entered by hand${form.createdBy ? ` by ${form.createdBy.name}` : ''} ${formatDateTime(form.receivedAt)}`
              : `Received ${formatDateTime(form.receivedAt)}`}
            <span className="font-mono text-xs">· {form.id}</span>
          </span>
        }
        actions={
          <>
            <PaymentBadge tag={form.paymentTag} />
            <ReviewBadge done={form.intakeReviewed} by={form.intakeReviewedBy?.name} />
            <StartRunButton form={form} />
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <IntakeCard form={form} />
        </div>
        <div className="space-y-6">
          <ContactCard form={form} />
          <PaymentCard form={form} />
          <IntakeReview form={form} />
          <HistoryCard formId={form.id} />
          <DeleteFormCard form={form} />
        </div>
      </div>
    </>
  );
}

function IntakeCard({ form }: { form: FormDetail }) {
  const { user } = useMe();
  const { updateIntake } = useFormMutations(form.id);
  const [mode, setMode] = useState<'view' | 'edit' | 'compare'>('view');
  const canEdit = can(user.role, 'forms.edit') && !form.lockedAt;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Intake</CardTitle>
        {mode !== 'edit' && (
          <div className="flex gap-2">
            {form.edited && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode(mode === 'compare' ? 'view' : 'compare')}
              >
                {mode === 'compare' ? 'Hide original' : 'Show original'}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" onClick={() => setMode('edit')}>
                <Pencil /> Edit
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {form.lockedAt && (
          <Alert>
            <Lock className="size-4" />
            <AlertDescription>
              The intake was sent to Atlas on {formatDateTime(form.lockedAt)} and can no longer be
              edited.
            </AlertDescription>
          </Alert>
        )}
        {mode === 'edit' && form.intakeReviewed && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Saving changes puts the intake review back to pending.
            </AlertDescription>
          </Alert>
        )}

        {mode === 'edit' ? (
          <IntakeEditor
            intake={form.rawIntake}
            saving={updateIntake.isPending}
            onCancel={() => setMode('view')}
            onSave={(next) =>
              updateIntake.mutate(next, {
                onSuccess: () => {
                  toastSuccess('Intake saved');
                  setMode('view');
                },
                onError: (err) => toastError(err),
              })
            }
          />
        ) : mode === 'compare' ? (
          <div className="grid gap-6 xl:grid-cols-2">
            <div>
              <p className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                As received
              </p>
              <IntakeView intake={form.originalIntake} />
            </div>
            <div>
              <p className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                With staff edits
              </p>
              <IntakeView intake={form.rawIntake} />
            </div>
          </div>
        ) : (
          <>
            {form.edited && <p className="text-xs text-muted-foreground">Includes staff edits.</p>}
            <IntakeView intake={form.rawIntake} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

const ContactFormSchema = z.object({
  name: z.string().max(200),
  email: z.union([z.literal(''), z.email('Enter a valid email')]),
  phone: z.string().max(50),
  company: z.string().max(200),
  jobTitle: z.string().max(200),
});

function ContactCard({ form }: { form: FormDetail }) {
  const { user } = useMe();
  const { updateContact } = useFormMutations(form.id);
  const [editing, setEditing] = useState(false);
  const c = form.contactDetail;
  const values = {
    name: c.name ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    company: c.company ?? '',
    jobTitle: c.jobTitle ?? '',
  };
  const rhf = useForm<z.infer<typeof ContactFormSchema>>({
    resolver: zodResolver(ContactFormSchema),
    values,
  });

  function save(v: z.infer<typeof ContactFormSchema>) {
    const toNull = (s: string) => (s.trim() ? s.trim() : null);
    const patch: Contact = {
      name: toNull(v.name),
      email: toNull(v.email),
      phone: toNull(v.phone),
      company: toNull(v.company),
      jobTitle: toNull(v.jobTitle),
    };
    ContactSchema.parse(patch);
    updateContact.mutate(patch, {
      onSuccess: () => {
        toastSuccess('Contact saved');
        setEditing(false);
      },
      onError: (err) => toastError(err),
    });
  }

  const rows: [keyof typeof values, string][] = [
    ['name', 'Name'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['company', 'Company'],
    ['jobTitle', 'Job title'],
  ];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">Contact</CardTitle>
        {!editing && can(user.role, 'forms.edit') && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <Form {...rhf}>
            <form onSubmit={rhf.handleSubmit(save)} className="space-y-3" noValidate>
              {rows.map(([name, label]) => (
                <FormField
                  key={name}
                  control={rhf.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{label}</FormLabel>
                      <FormControl>
                        <Input type={name === 'email' ? 'email' : 'text'} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={updateContact.isPending}>
                  Save
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <>
            <dl className="grid grid-cols-[5.5rem_1fr] gap-y-2 text-sm">
              {rows.map(([name, label]) => (
                <div key={name} className="contents">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="break-all">
                    {values[name] || <span className="text-muted-foreground">-</span>}
                  </dd>
                </div>
              ))}
            </dl>
            {!c.email && (
              <p className="mt-3 flex items-center gap-1.5 text-xs text-warning">
                <AlertTriangle className="size-3.5" /> No email yet - the credentials email cannot
                be sent without one.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentCard({ form }: { form: FormDetail }) {
  const { user } = useMe();
  const { setPayment } = useFormMutations(form.id);
  const [next, setNext] = useState<PaymentTag | null>(null);
  const allowed = can(user.role, 'forms.payment');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Payment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <RadioGroup
          value={form.paymentTag}
          onValueChange={(v) => setNext(v as PaymentTag)}
          disabled={!allowed || setPayment.isPending}
          aria-label="Payment tag"
        >
          {PAYMENT_TAGS.map((t) => (
            <div key={t} className="flex items-center gap-2">
              <RadioGroupItem value={t} id={`pay-${t}`} />
              <Label htmlFor={`pay-${t}`} className="font-normal">
                {PAYMENT_LABELS[t]}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {form.paymentTagSetAt && (
          <p className="text-xs text-muted-foreground">
            Set by {form.paymentTagSetBy?.name ?? 'unknown'} ·{' '}
            {formatDateTime(form.paymentTagSetAt)}
          </p>
        )}
        {form.paymentTag === 'cancelled' && (
          <Alert variant="destructive">
            <AlertDescription>Cancelled forms can never be started.</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <ConfirmDialog
        open={next !== null}
        onOpenChange={(o) => !o && setNext(null)}
        title={`Mark as “${next ? PAYMENT_LABELS[next] : ''}”?`}
        description={
          next === 'paid'
            ? 'A paid, reviewed intake can be started.'
            : next === 'cancelled'
              ? 'A cancelled form can never be started.'
              : 'The form cannot be started until it is marked paid.'
        }
        confirmLabel="Confirm"
        destructive={next === 'cancelled'}
        pending={setPayment.isPending}
        onConfirm={() =>
          next &&
          setPayment.mutate(next, {
            onSuccess: () => {
              toastSuccess('Payment updated');
              setNext(null);
            },
            onError: (err) => toastError(err),
          })
        }
      />
    </Card>
  );
}

/** Developers only, and only once the form is cancelled. The API enforces both. */
function DeleteFormCard({ form }: { form: FormDetail }) {
  const { user } = useMe();
  const navigate = useNavigate();
  const { remove } = useFormMutations(form.id);
  const [confirming, setConfirming] = useState(false);
  if (!can(user.role, 'forms.delete') || form.paymentTag !== 'cancelled') return null;

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base">Delete form</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Removes this cancelled form, its contact and any cancelled or failed runs for good. The
          activity log keeps a record of what was deleted.
        </p>
        <Button variant="destructive" size="sm" onClick={() => setConfirming(true)}>
          <Trash2 /> Delete form
        </Button>
      </CardContent>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete this form?"
        description={`${form.company} and everything under it will be removed from the panel. This cannot be undone. Type the company name to confirm.`}
        typeToConfirm={form.company}
        confirmLabel="Delete form"
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => {
              toastSuccess('Form deleted');
              setConfirming(false);
              void navigate({ to: '/forms' });
            },
            onError: (err) => toastError(err),
          })
        }
      />
    </Card>
  );
}

function IntakeReview({ form }: { form: FormDetail }) {
  const { user } = useMe();
  const { review, undoReview } = useFormMutations(form.id);
  const allowed = can(user.role, 'forms.review');
  return (
    <ReviewCard
      title="Intake review"
      description="Required before the run can be started."
      reviewedAt={form.intakeReviewedAt}
      reviewedBy={form.intakeReviewedBy?.name ?? null}
      notes={form.intakeReviewNotes}
      blockedReason={
        !allowed
          ? 'Your role cannot review intakes'
          : form.lockedAt
            ? 'The run has started'
            : !form.intake
              ? 'Complete the company and its departments first (Edit)'
              : null
      }
      undoBlockedReason={form.lockedAt ? 'The run has started' : null}
      pending={review.isPending || undoReview.isPending}
      onReview={(notes) =>
        review.mutate(notes, {
          onSuccess: () => toastSuccess('Intake marked reviewed'),
          onError: (err) => toastError(err),
        })
      }
      onUndo={
        allowed
          ? () => undoReview.mutate(undefined, { onError: (err) => toastError(err) })
          : undefined
      }
    />
  );
}

function HistoryCard({ formId }: { formId: string }) {
  const [page, setPage] = useState(1);
  const history = useQuery(historyQuery('form', formId, page));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">History</CardTitle>
      </CardHeader>
      <CardContent>
        {history.data ? (
          <>
            <AuditTimeline entries={history.data.history} />
            <Pagination info={history.data} onPageChange={setPage} className="mt-6" />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
      </CardContent>
    </Card>
  );
}
